import { Link } from 'react-router-dom';
import { money } from '../lib/format';
import { VideoPoster } from './VideoPoster';

/**
 * One property for sale, as a card.
 *
 * Deliberately NOT `ListingCard` with a mode prop. That component writes
 * "/month" beside its price and leads with bedrooms; a sale card leads with an
 * asking price that may be negotiable, and for a plot of land there are no
 * bedrooms to lead with at all — it is land size and tenure that decide whether
 * anybody clicks. One component serving both would be two components sharing an
 * `if`, and the `if` would be in every line.
 */

export interface SalePanorama { id: string; section: string; label: string | null }

export interface PublicSale {
  id: string;
  title: string;
  description: string | null;
  property_type: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  county: string | null;
  estate_area: string | null;
  asking_price: string;
  price_negotiable: boolean;
  land_size: string | null;
  land_size_unit: string | null;
  floor_area_sqm: string | null;
  tenure: string;
  lease_years_remaining: number | null;
  title_deed_status: string;
  year_built: number | null;
  service_charge_monthly: string | null;
  mortgage_accepted: boolean | null;
  amenities: string[];
  /** published · reserved · sold — all three are on the site. */
  status: string;
  published_at: string | null;
  /**
   * What the seller paid for placement (migration 128): 1 ordinary, 2 raised,
   * 3 top. Labelled on the card in words, because a buyer is entitled to know
   * that a property is high up because it was paid for and not because it fits
   * them best — the same promise the rental card makes.
   */
  placement_tier?: number;
  is_featured?: boolean;
  /**
   * When a person at NyumbaSuite checked that this seller may sell this
   * property (migration 130). A DATE, never a boolean — see verifiedLine().
   */
  verified_at?: string | null;
  verified_capacity?: string | null;
  photo_ids: string[] | null;
  panoramas?: SalePanorama[] | null;
  video_id?: string | null;
}

/** Public, immutable bytes: cached for a year by the route's own header.
 *  Default is the original; the card passes 'thumb' (145), as ListingCard
 *  does for rentals, so the detail gallery keeps full resolution. */
export const salePhotoUrl = (id: string, size: 'full' | 'thumb' = 'full') =>
  `/api/v1/sales/photos/${id}${size === 'thumb' ? '?size=thumb' : ''}`;

/*
 * verifiedLine was DEFINED here, byte-identical to the copy in packages/shared
 * that the phone uses. Two copies of a sentence carrying legal weight is how
 * they come to say different things, and the shared file makes that argument
 * itself: take both or take neither. Re-exported rather than moved outright,
 * because SaleDetail imports it from this module.
 */
export { verifiedLine } from '../lib/saleverification';

/** `semi_detached` → `Semi detached`. The API stores codes; people read words. */
export const humanise = (s: string | null | undefined) =>
  !s ? '' : s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * Land size, with its unit spelled out.
 *
 * The unit is never dropped: a quarter of an acre and a quarter of a hectare
 * differ by two and a half times, and a number without its unit is the one
 * mistake this field can make that costs somebody real money.
 */
export function landSize(size: string | null, unit: string | null): string | null {
  if (!size || !unit) return null;
  const n = Number(size);
  if (!Number.isFinite(n)) return null;
  const shown = n.toLocaleString('en-KE', { maximumFractionDigits: 3 });
  return `${shown} ${unit === 'sqm' ? 'm²' : unit}`;
}

export function SaleCard({ listing: l }: { listing: PublicSale }) {
  const photo = l.photo_ids?.[0];
  const where = [l.estate_area, l.county].filter(Boolean).join(', ');
  const gone = l.status === 'sold' || l.status === 'reserved';
  const land = landSize(l.land_size, l.land_size_unit);

  return (
    <Link to={`/buy/${l.id}`} className={`lst-card${gone ? ' lst-card-taken' : ''}`}>
      <div className="lst-photo">
        {photo
          ? <img src={salePhotoUrl(photo, 'thumb')} alt="" loading="lazy" />
          : l.video_id
            ? <VideoPoster src={`/api/v1/sales/videos/${l.video_id}`} />
            : <div className="lst-photo-none" aria-hidden>⌂</div>}

        {/* Read before the price is: whoever is scanning the grid should learn
            this one is gone without opening it. */}
        {gone && (
          <span className="lst-taken-badge">{l.status === 'sold' ? 'Sold' : 'Under offer'}</span>
        )}
        {(l.photo_ids?.length ?? 0) > 1 && !gone && (
          <span className="lst-photo-count">{l.photo_ids!.length} photos</span>
        )}
        {!gone && ((l.panoramas?.length ?? 0) > 0 || l.video_id) && (
          <span className="lst-media-badges">
            {(l.panoramas?.length ?? 0) > 0 && <span className="lst-media-badge">360°</span>}
            {l.video_id && <span className="lst-media-badge">▶ Video</span>}
          </span>
        )}
      </div>

      <div className="lst-body">
        {/* Never on a property that has gone: "Featured · Sold" would be
            advertising a disappointment. */}
        {(l.placement_tier ?? 1) > 1 && !gone && (
          <span className="lst-featured">
            {(l.placement_tier ?? 1) >= 3 ? 'Premium' : 'Featured'}
          </span>
        )}
        {/* Checked is a different kind of fact from paid-for, so it reads
            differently: no accent colour, no shouting — a quiet statement of
            work done. The detail page carries the date and the caveat. */}
        {l.verified_at && !gone && <span className="lst-checked">Seller checked</span>}
        <p className="lst-rent">
          {money(l.asking_price)}
          {l.price_negotiable && <span className="lst-per">negotiable</span>}
        </p>
        <h3 className="lst-title">{l.title}</h3>
        {where && <p className="lst-where">{where}</p>}

        <p className="lst-facts">
          {/* Land has no bedrooms, and padding the line with "0 bed" to keep
              every card the same shape would be a lie about the property. */}
          {l.bedrooms !== null && <span>{l.bedrooms} bed</span>}
          {l.bathrooms !== null && <span>{l.bathrooms} bath</span>}
          {land && <span>{land}</span>}
          {l.property_type && <span className="lst-type">{humanise(l.property_type)}</span>}
        </p>
      </div>
    </Link>
  );
}
